/**
 * booking-confirmation — client for resending a booking's confirmation email,
 * with an optional correction of the customer's email on file (BON-630).
 *
 * `POST /api/v2/bookings/:id/resend-confirmation`. Absent `customerEmail`
 * resends to the email already on file; present, it is compared server-side
 * (normalized) against the stored address — equal means no correction, a
 * plain resend.
 *
 * A FAILED SEND IS A STATUS CODE, NOT A 200. Unlike the reservations rotation
 * endpoints (`../../api/reservations.ts`), a provider failure here answers
 * **502 `EMAIL_SEND_FAILED`** (frozen contract, BON-630 round 1) rather than a
 * 200 carrying `emailed: false` — so callers branch on HTTP status, not on a
 * body flag.
 *
 * `code` is populated ONLY for the two statuses the contract types it for
 * (409's four refusals, 502). The contract is explicit that FE must not rely
 * on `code` for 400/403/404 — an existing shared middleware shape may answer
 * those, with no `code` field at all — so this client never reads one off
 * those responses, even if a response happened to carry one.
 */
import { apiFetch } from '../lib/apiFetch';

/** The 409 refusals, plus the one 5xx that is typed rather than generic
 *  (BON-630 contract). Never populated for 400/403/404 — see file docblock. */
export type ResendConfirmationErrorCode =
  | 'BOOKING_NOT_CONFIRMED'
  | 'RECENTLY_RESENT'
  | 'EMAIL_IN_USE'
  | 'NO_CUSTOMER_EMAIL'
  | 'EMAIL_SEND_FAILED';

/**
 * The status a request that never got an answer is reported with — a dropped
 * connection or similar rejects `fetch` itself rather than returning a
 * `Response`, so there is no HTTP status to read. Mirrors
 * `../../api/reservations.ts`'s constant of the same name; kept local rather
 * than imported so this module has no dependency on the reservations slice.
 */
export const NO_RESPONSE_STATUS = 0;

/** Thrown by {@link resendBookingConfirmation} so callers can branch on the
 *  HTTP status (and, where the contract types one, the refusal `code`). */
export class BookingConfirmationError extends Error {
  constructor(
    message: string,
    /** The HTTP status, or {@link NO_RESPONSE_STATUS} when nothing answered. */
    readonly status: number,
    /** Present only on a 409 refusal or the 502 send failure. */
    readonly code?: ResendConfirmationErrorCode,
    /** Present only alongside the 502: whether the customer's email on file
     *  was changed by this request before the send was attempted and failed. */
    readonly contactUpdated?: boolean,
  ) {
    super(message);
    this.name = 'BookingConfirmationError';
  }
}

export interface ResendBookingConfirmationBody {
  customerEmail?: string;
}

export interface ResendBookingConfirmationResult {
  status: 'resent';
  /** The recipient address the email was actually sent to. */
  emailedTo: string;
  /** True iff the customer's email on file was changed by this request. */
  contactUpdated: boolean;
}

interface ErrorEnvelope {
  status?: string;
  code?: string;
  message?: string;
  contactUpdated?: boolean;
}

/** The codes this client will actually surface as {@link BookingConfirmationError.code}
 *  — a closed set, so an unrecognized string from the server (or a body from
 *  shared middleware that reuses the field name for something else) degrades
 *  to `undefined` rather than lying about its type. */
const KNOWN_CODES: readonly ResendConfirmationErrorCode[] = [
  'BOOKING_NOT_CONFIRMED',
  'RECENTLY_RESENT',
  'EMAIL_IN_USE',
  'NO_CUSTOMER_EMAIL',
  'EMAIL_SEND_FAILED',
];

function fallbackFor(status: number): string {
  if (status === 400) return 'Enter a valid email address.';
  if (status === 403) return "This booking isn't in a region you can act on.";
  if (status === 404) return 'That booking could not be found.';
  if (status === 409) return 'Nothing was sent.';
  if (status === 502) return 'The confirmation email could not be sent.';
  return 'Something went wrong. Please try again.';
}

async function errorFrom(res: Response): Promise<BookingConfirmationError> {
  const body = (await res.json().catch(() => ({}))) as ErrorEnvelope;
  const message = body.message || fallbackFor(res.status);
  // Only 409 and 502 are typed with a `code` in the contract — 400/403/404 may
  // come from shared middleware whose body shape this client must not depend
  // on, so `code` is left undefined for every other status regardless of what
  // the body happens to contain.
  const code =
    (res.status === 409 || res.status === 502) &&
    KNOWN_CODES.includes(body.code as ResendConfirmationErrorCode)
      ? (body.code as ResendConfirmationErrorCode)
      : undefined;
  const contactUpdated = typeof body.contactUpdated === 'boolean' ? body.contactUpdated : undefined;
  return new BookingConfirmationError(message, res.status, code, contactUpdated);
}

/**
 * `apiFetch`, with a rejection turned into a {@link BookingConfirmationError}
 * carrying {@link NO_RESPONSE_STATUS} — see the analogous `rotationFetch` in
 * `../../api/reservations.ts` for why this must not collapse into a bare
 * `Error` or a 5xx: the caller's "not sure whether that went through" wording
 * is keyed off this status.
 */
async function safeFetch(url: string, options: RequestInit): Promise<Response> {
  try {
    return await apiFetch(url, options);
  } catch {
    throw new BookingConfirmationError('We could not reach the server.', NO_RESPONSE_STATUS);
  }
}

/** Resend a CONFIRMED booking's confirmation email, optionally correcting the
 *  customer's email on file first. */
export async function resendBookingConfirmation(
  bookingId: string,
  body: ResendBookingConfirmationBody = {},
): Promise<ResendBookingConfirmationResult> {
  const res = await safeFetch(`/api/v2/bookings/${bookingId}/resend-confirmation`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
  if (!res.ok) throw await errorFrom(res);
  return (await res.json()) as ResendBookingConfirmationResult;
}
